import { pickedPointAtom } from '@/atoms';
import { execute } from '@/graphql/execute';
import { useOrganizations } from '@/hooks/useOrganizations';
import { updateProjectMutation } from '@/mutations/project.mutation';
import { getProjectQuery } from '@/queries/project.query';
import { CenterFocus } from '@mynaui/icons-react';
import { DropdownMenu } from '@radix-ui/react-dropdown-menu';
import { useAtom } from 'jotai';
import { Copy } from 'lucide-react';
import { FunctionComponent, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem } from './ui/dropdown-menu';

export const PointCloudContextMenu: FunctionComponent = () => {
  const [pickedPoint, setPickedPoint] = useAtom(pickedPointAtom);
  const { hasRole } = useOrganizations();
  const { projectId } = useParams();

  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      // Only prevent default if we have a picked point
      if (pickedPoint?.position) {
        e.preventDefault();
      }
    };

    document.body.addEventListener('contextmenu', handleContextMenu);

    return () => {
      document.body.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [pickedPoint]);

  const handleCopyCoordinates = () => {
    if (!pickedPoint?.position) {
      toast.error('No point selected');
      return;
    }

    const position = pickedPoint.position;
    const coordinates = `${position.x.toFixed(3)}, ${position.y.toFixed(3)}, ${position.z.toFixed(3)}`;

    navigator.clipboard
      .writeText(coordinates)
      .then(() => {
        toast.success('Coordinates copied to clipboard');
      })
      .catch(() => {
        toast.error('Failed to copy coordinates');
      });
  };

  const _handleInitialPosition = async () => {
    const projectData = await execute(getProjectQuery, { id: projectId! });
    if (!projectData) {
      toast.error('Project not found');
      return;
    }

    const initialPosition = projectData.project.initialPosition || {};
    if (!initialPosition) throw new Error('Initial position not found');
    initialPosition.coords = [pickedPoint?.position?.x, pickedPoint?.position?.y, pickedPoint?.position?.z];
    await execute(updateProjectMutation, {
      input: {
        id: projectId!,
        initialPosition,
        initialAngle: parseInt(pickedPoint?.cameraAngle.toFixed(0) || '0', 10),
      },
    });
  };

  const _handleSetInitialPosition = async () => {
    toast.promise(_handleInitialPosition, {
      loading: 'Setting initial position...',
      success: 'Initial position set',
      error: 'Failed to set initial position',
    });
  };

  if (!pickedPoint?.position) return null;
  return (
    <DropdownMenu
      open
      onOpenChange={(open) => {
        if (!open) setPickedPoint(null);
      }}>
      <DropdownMenuContent
        className='w-64'
        style={{ left: pickedPoint?.screenX, top: pickedPoint?.screenY, position: 'fixed', zIndex: 1000 }}>
        <DropdownMenuGroup>
          <DropdownMenuItem onClick={handleCopyCoordinates} disabled={!pickedPoint?.position}>
            <Copy className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
            Copy Coordinates
          </DropdownMenuItem>
          {hasRole('EDITOR') && (
            <DropdownMenuItem onClick={_handleSetInitialPosition} disabled={!pickedPoint?.position}>
              <CenterFocus className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
              Set as Initial Position
            </DropdownMenuItem>
          )}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
